import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Assessment as ReportIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Schedule as ScheduleIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Timeline as TimelineIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Speed as PerformanceIcon,
  BugReport as BugIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Print as PrintIcon,
  GetApp as ExportIcon,
} from '@mui/icons-material';
import ReportsService, { Report, ReportTemplate, ReportSchedule, ReportStatistics } from '../../services/ReportsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * Reports Component for Project Manager
 * Manages report generation, scheduling, and distribution
 */
const Reports: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
  const [metrics, setMetrics] = useState<ReportStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [filterType, setFilterType] = useState('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [generatingReports, setGeneratingReports] = useState<Set<number>>(new Set());

  const projects = ['E-commerce Platform Redesign', 'Mobile App Development', 'Dashboard Analytics Implementation'];

  useEffect(() => {
    loadReportsData();
  }, []);

  const loadReportsData = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Load reports, templates, schedules, and statistics from API
      const [reportsData, templatesData, schedulesData, statisticsData] = await Promise.all([
        ReportsService.getReports(),
        ReportsService.getTemplates(),
        ReportsService.getSchedules(),
        ReportsService.getStatistics(),
      ]);
      
      setReports(reportsData);
      setTemplates(templatesData);
      setSchedules(schedulesData);
      setMetrics(statisticsData);
    } catch (err) {
      setError('Failed to load reports data. Please try again.');
      console.error('Error loading reports data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, report: Report) => {
    setAnchorEl(event.currentTarget);
    setSelectedReport(report);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReport(null);
  };

  const handleGenerateReport = async (reportId?: number, templateId?: number) => {
    try {
      if (!templateId) {
        // If no template ID provided, assume we're regenerating an existing report
        const report = reports.find(r => r.id === reportId);
        if (!report) return;
        templateId = report.template_id;
      }

      if (reportId) {
        setGeneratingReports(prev => new Set(prev).add(reportId));
      }

      // In a real implementation, you would get these values from the UI
      await ReportsService.createReport({
        template_id: templateId,
        title: 'Generated Report',
        format: 'pdf',
      });

      // Reload reports data
      await loadReportsData();

      if (reportId) {
        setGeneratingReports(prev => {
          const newSet = new Set(prev);
          newSet.delete(reportId);
          return newSet;
        });
      }
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Failed to generate report. Please try again.');
      if (reportId) {
        setGeneratingReports(prev => {
          const newSet = new Set(prev);
          newSet.delete(reportId);
          return newSet;
        });
      }
    }
  };

  const handleDownloadReport = async (reportId: number) => {
    try {
      await ReportsService.downloadReport(reportId);
    } catch (err) {
      console.error('Error downloading report:', err);
      setError('Failed to download report. Please try again.');
    }
  };

  const handleShareReport = async (reportId: number) => {
    try {
      const result = await ReportsService.shareReport(reportId);
      alert(`Share URL: ${result.share_url}`);
      handleMenuClose();
    } catch (err) {
      console.error('Error sharing report:', err);
      setError('Failed to share report. Please try again.');
    }
  };

  const handleDeleteReport = async (reportId: number) => {
    try {
      await ReportsService.deleteReport(reportId);
      await loadReportsData();
      handleMenuClose();
    } catch (err) {
      console.error('Error deleting report:', err);
      setError('Failed to delete report. Please try again.');
    }
  };

  const getReportTypeIcon = (type: string) => {
    switch (type) {
      case 'project-summary': return <ReportIcon />;
      case 'performance': return <PerformanceIcon />;
      case 'budget': return <MoneyIcon />;
      case 'team-productivity': return <PeopleIcon />;
      case 'quality': return <BugIcon />;
      default: return <ReportIcon />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ready': return <SuccessIcon color="success" />;
      case 'generating': return <CircularProgress size={20} />;
      case 'error': return <WarningIcon color="error" />;
      default: return <SuccessIcon />;
    }
  };

  const getFormatIcon = (format: string) => {
    switch (format) {
      case 'pdf': return <PrintIcon />;
      case 'excel': return <ExportIcon />;
      case 'csv': return <ExportIcon />;
      case 'html': return <ViewIcon />;
      default: return <ExportIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getFilteredReports = () => {
    if (filterType === 'all') return reports;
    return reports.filter(report => report.type === filterType);
  };

  const reportTemplates = [
    {
      type: 'project-summary',
      title: 'Project Summary Report',
      description: 'Overview of project progress, milestones, and deliverables',
      icon: <ReportIcon />
    },
    {
      type: 'team-productivity',
      title: 'Team Productivity Report',
      description: 'Team performance metrics and productivity analysis',
      icon: <PeopleIcon />
    },
    {
      type: 'budget',
      title: 'Budget Report',
      description: 'Financial tracking and budget utilization analysis',
      icon: <MoneyIcon />
    },
    {
      type: 'quality',
      title: 'Quality Assurance Report',
      description: 'Testing results, bug reports, and quality metrics',
      icon: <BugIcon />
    },
    {
      type: 'performance',
      title: 'Performance Report',
      description: 'System performance monitoring and optimization insights',
      icon: <PerformanceIcon />
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Reports & Analytics
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<ScheduleIcon />}
          >
            Schedule Report
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Report
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={loadReportsData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      )}

      {/* Metrics Overview */}
      {metrics && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 2,
            mb: 3
          }}
        >
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Reports
              </Typography>
              <Typography variant="h4">{metrics.reports.total}</Typography>
              <Typography variant="body2" color="text.secondary">
                {metrics.reports.ready} ready, {metrics.reports.pending} pending
              </Typography>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active Schedules
              </Typography>
              <Typography variant="h4">{metrics.schedules.active}</Typography>
              <Typography variant="body2" color="text.secondary">
                {metrics.schedules.due} due to run
              </Typography>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Downloads
              </Typography>
              <Typography variant="h4">{metrics.engagement.total_downloads}</Typography>
              <Typography variant="body2" color="text.secondary">
                Avg {metrics.engagement.avg_downloads_per_report.toFixed(1)} per report
              </Typography>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Recent Reports
              </Typography>
              <Typography variant="h4">{metrics.reports.recent_30_days}</Typography>
              <Typography variant="body2" color="text.secondary">
                Last 30 days
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="reports tabs">
            <Tab label="All Reports" />
            <Tab label="Templates" />
            <Tab label="Scheduled" />
            <Tab label="Analytics" />
          </Tabs>
        </Box>

        {/* All Reports Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box display="flex" gap={2} alignItems="center" sx={{ mb: 3 }} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={filterType}
                label="Report Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="project-summary">Project Summary</MenuItem>
                <MenuItem value="team-productivity">Team Productivity</MenuItem>
                <MenuItem value="budget">Budget</MenuItem>
                <MenuItem value="quality">Quality</MenuItem>
                <MenuItem value="performance">Performance</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<ReportIcon />}
              onClick={() => reports.forEach(report => handleGenerateReport(report.id))}
            >
              Generate All
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Report</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Generated</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredReports().map((report) => (
                  <TableRow key={report.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {report.title}
                        </Typography>
                        {report.description && (
                          <Typography variant="caption" color="text.secondary">
                            {report.description}
                          </Typography>
                        )}
                        {report.project_name && (
                          <Box mt={0.5}>
                            <Chip 
                              label={report.project_name} 
                              size="small" 
                              variant="outlined"
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getReportTypeIcon(report.type)}
                        {report.type.replace('-', ' ')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getFormatIcon(report.format)}
                        {report.format.toUpperCase()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {generatingReports.has(report.id) ? (
                          <CircularProgress size={16} />
                        ) : (
                          getStatusIcon(report.status)
                        )}
                        <Chip 
                          label={generatingReports.has(report.id) ? 'generating' : report.status} 
                          size="small"
                          color={
                            generatingReports.has(report.id) ? 'info' :
                            report.status === 'ready' ? 'success' :
                            report.status === 'error' ? 'error' : 'default'
                          }
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {report.generated_at ? formatDate(report.generated_at) : '-'}
                    </TableCell>
                    <TableCell>{report.file_size || '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleGenerateReport(report.id)}
                        disabled={generatingReports.has(report.id)}
                        title="Regenerate"
                      >
                        <ReportIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={report.status !== 'ready'}
                        onClick={() => handleDownloadReport(report.id)}
                        title="Download"
                      >
                        <DownloadIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, report)}
                        title="More options"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Templates Tab */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Report Templates
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose from pre-built report templates to quickly generate insights
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)'
              },
              gap: 2
            }}
          >
            {templates.map((template) => (
              <Card key={template.id} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    {getReportTypeIcon(template.type)}
                    <Typography variant="h6">{template.name}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {template.description}
                  </Typography>
                  <Box mt={1}>
                    <Chip 
                      label={template.is_system ? 'System' : 'Custom'} 
                      size="small" 
                      color={template.is_system ? 'primary' : 'default'}
                    />
                    <Chip 
                      label={`${template.reports_count} reports`} 
                      size="small" 
                      sx={{ ml: 1 }}
                    />
                  </Box>
                  <Button 
                    variant="contained" 
                    size="small" 
                    sx={{ mt: 2 }}
                    onClick={() => handleGenerateReport(undefined, template.id)}
                  >
                    Use Template
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        </TabPanel>

        {/* Scheduled Tab */}
        <TabPanel value={activeTab} index={2}>
          <List>
            {schedules.map((schedule, index) => (
              <React.Fragment key={schedule.id}>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon color={schedule.is_active ? 'primary' : 'disabled'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        {schedule.name}
                        {schedule.is_due && (
                          <Chip label="Due" size="small" color="warning" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        {schedule.description && (
                          <Typography variant="body2" color="text.secondary">
                            {schedule.description}
                          </Typography>
                        )}
                        <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                          <Chip 
                            label={`Every ${schedule.frequency}`} 
                            size="small" 
                            color="primary"
                          />
                          <Chip 
                            label={`${schedule.format.toUpperCase()}`} 
                            size="small" 
                          />
                          {schedule.success_rate !== null && (
                            <Chip 
                              label={`${schedule.success_rate}% success`} 
                              size="small" 
                              color={schedule.success_rate >= 90 ? 'success' : schedule.success_rate >= 70 ? 'warning' : 'error'}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Next run: {new Date(schedule.next_run_at).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end">
                      <MoreVertIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < schedules.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={activeTab} index={3}>
          <Box textAlign="center" py={4}>
            <BarChartIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Report Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Detailed analytics about report usage and performance will be displayed here.
            </Typography>
          </Box>
        </TabPanel>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem 
          onClick={() => {
            if (selectedReport) handleDownloadReport(selectedReport.id);
            handleMenuClose();
          }}
          disabled={selectedReport?.status !== 'ready'}
        >
          <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
          Download
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedReport) handleShareReport(selectedReport.id);
          }}
          disabled={selectedReport?.status !== 'ready'}
        >
          <ShareIcon sx={{ mr: 1 }} fontSize="small" />
          Share
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => {
            if (selectedReport && window.confirm('Are you sure you want to delete this report?')) {
              handleDeleteReport(selectedReport.id);
            }
          }} 
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Create Report Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Report</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField label="Report Title" fullWidth />
            <TextField label="Description" multiline rows={3} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Report Type</InputLabel>
              <Select defaultValue="project-summary" label="Report Type">
                <MenuItem value="project-summary">Project Summary</MenuItem>
                <MenuItem value="team-productivity">Team Productivity</MenuItem>
                <MenuItem value="budget">Budget Analysis</MenuItem>
                <MenuItem value="quality">Quality Assurance</MenuItem>
                <MenuItem value="performance">Performance</MenuItem>
                <MenuItem value="custom">Custom Report</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Format</InputLabel>
              <Select defaultValue="pdf" label="Format">
                <MenuItem value="pdf">PDF</MenuItem>
                <MenuItem value="excel">Excel</MenuItem>
                <MenuItem value="csv">CSV</MenuItem>
                <MenuItem value="html">HTML</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <Select defaultValue="" label="Project">
                <MenuItem value="">All Projects</MenuItem>
                {projects.map(project => (
                  <MenuItem key={project} value={project}>{project}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Create Report</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reports;